import React, { useState, useEffect } from "react";
import { FiSearch, FiArrowRight, FiCheckCircle, FiXCircle, FiCalendar, FiClock, FiX, FiUser } from "react-icons/fi";
import { LuMessageSquare } from "react-icons/lu";
import { useDispatch, useSelector } from "react-redux";
import { getAllCorrections, updateCorrectionStatus } from "../../../Redux/Slice/AttendanceCorrection";
import moment from "moment";
import Loader from "../../../Components/Loader/Loader";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { TablePagination } from "@mui/material";
const AttendanceCorrections = () => {
    const dispatch = useDispatch();
    const { allCorrections, loading } = useSelector((state) => state.AttendanceCorrection);

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [adminNote, setAdminNote] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    useEffect(() => {
        dispatch(getAllCorrections());
    }, [dispatch]);

    const getStatusStyle = (status) => {
        switch (status.toLowerCase()) {
            case 'approved': return 'bg-emerald-50 text-emerald-600 border-emerald-100';
            case 'rejected': return 'bg-red-50 text-red-500 border-red-100';
            case 'pending': return 'bg-orange-50 text-orange-500 border-orange-100';
            default: return 'bg-gray-50 text-gray-500 border-gray-100';
        }
    };

    const getStatusIcon = (status) => {
        switch (status.toLowerCase()) {
            case 'approved': return <FiCheckCircle size={12} />;
            case 'rejected': return <FiXCircle size={12} />;
            default: return <FiClock size={12} />;
        }
    };

    const handleOpenReview = (request) => {
        setSelectedRequest(request);
        setAdminNote(request.adminNote || "");
        setIsModalOpen(true);
    };

    const handleAction = (status) => {
        if (!adminNote.trim()) {
            toast.warn("Please provide an admin comment before submitting.", {
                position: "top-right",
                autoClose: 3000,
            });
            return;
        }

        dispatch(updateCorrectionStatus({
            id: selectedRequest._id,
            status,
            adminNote
        })).then((res) => {
            if (res.payload?.status === "Success") {
                toast.success(`Request ${status} successfully!`, {
                    position: "top-right",
                    autoClose: 3000,
                });
                setIsModalOpen(false);
                dispatch(getAllCorrections());
            } else {
                toast.error("Something went wrong. Please try again.", {
                    position: "top-right",
                    autoClose: 3000,
                });
            }
        });
    };

    const filteredRequests = allCorrections.filter(req =>
        req.userId?.Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.reason?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading && allCorrections.length === 0) return <Loader />;

    return (
        <div className="px-3 py-6 min-h-0 bg-gradient-to-br from-slate-50/50 via-white to-slate-50/20 font-sans">
            <div className="w-full space-y-6">

                {/* Page Title */}
                <div className="title mb-4">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Attendance Correction Portal
                    </h3>
                </div>

                {/* Header Section (Controls) */}
                <div className="bg-white p-4 rounded-2xl shadow-[0_2px_15px_rgba(0,0,0,0.03)] border border-gray-150 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-6">
                    <div className="flex items-center gap-5">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-indigo-650 rounded-xl flex items-center justify-center text-white shadow-lg shadow-indigo-100 border border-indigo-700">
                            <LuMessageSquare size={18} />
                        </div>
                        <div>
                            <h1 className="text-base sm:text-lg font-bold text-gray-900 tracking-tight">Active Requests</h1>
                            <p className="text-gray-600 text-xs font-semibold mt-0.5 capitalize tracking-wide">Review discrepancy requests</p>
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 w-full xl:w-auto">
                        <div className="relative flex-grow sm:flex-grow-0">
                            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                placeholder="Search by name or reason..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full sm:w-72 pl-11 pr-4 py-2.5 bg-gray-50 border border-gray-150 rounded-xl text-xs font-semibold text-gray-600 placeholder:text-gray-400 outline-none focus:ring-4 focus:ring-indigo-50 focus:border-indigo-200 transition-all"
                            />
                        </div>
                    </div>
                </div>

                {/* Main Table Content */}
                <div className="bg-white shadow-xl rounded-2xl overflow-hidden border border-gray-100 relative z-0 mt-4">
                    <div className="w-full overflow-x-auto" style={{ width: "100%" }}>
                        <div className="min-w-[1000px] overflow-hidden relative z-0">
                            <div className="max-h-[600px] overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                <table className="min-w-full table-auto border-collapse divide-y divide-gray-200 text-sm">
                                    <thead className="bg-[#133A69] text-white sticky top-0 z-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "200px" }}>Employee</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "150px" }}>Correction Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "250px" }}>Reason</th>
                                            <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>Applied On</th>
                                            <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>Status</th>
                                            <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider whitespace-nowrap" style={{ minWidth: "120px" }}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                                            <tr key={row._id} className="group transition-all duration-200 hover:shadow-md hover:bg-gray-50">
                                                <td className="px-6 py-4 align-top">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-sm shadow-sm ring-1 ring-indigo-100 overflow-hidden">
                                                            <img className='w-full h-full object-cover' src={row.userId?.ProfilePhoto} title={row.userId?.Name} alt={row.userId?.Name} />
                                                        </div>
                                                        <div>
                                                            <div className="text-[13px] font-bold text-gray-800 leading-tight group-hover:text-indigo-600 transition-colors">{row.userId?.Name}</div>
                                                            <div className="text-[11px] font-semibold text-gray-500 mt-0.5">{row.userId?.Email}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 align-top">
                                                    <div className="flex items-center gap-2 text-[12.5px] font-semibold text-gray-700">
                                                        <FiCalendar className="text-gray-400" size={13} />
                                                        {moment(row.correctionDate).format("D MMM YYYY")}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 align-top">
                                                    <p className="text-[13px] text-gray-600 font-medium italic leading-relaxed whitespace-pre-wrap bg-gray-50/50 p-2.5 rounded-lg border border-gray-100 min-h-[40px]">
                                                        "{row.reason}"
                                                    </p>
                                                </td>
                                                <td className="px-6 py-4 align-top text-[12.5px] font-bold text-gray-500 tabular-nums">
                                                    {moment(row.appliedDate).format("DD/MM/YYYY")}
                                                </td>
                                                <td className="px-6 py-4 align-top text-center whitespace-nowrap">
                                                    <span className={`inline-flex px-3 py-1 rounded-full text-[11px] font-bold tracking-wide border uppercase items-center gap-1.5 shadow-sm ${getStatusStyle(row.status)}`}>
                                                        {getStatusIcon(row.status)}
                                                        {row.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 align-top text-right">
                                                    <button
                                                        onClick={() => handleOpenReview(row)}
                                                        className="inline-flex items-center gap-1.5 text-[11px] font-black text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded uppercase tracking-widest transition-all shadow-sm group/btn"
                                                    >
                                                        Review
                                                        <FiArrowRight className="group-hover/btn:translate-x-1 transition-transform" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                        {filteredRequests.length === 0 && (
                                            <tr>
                                                <td colSpan="6" className="px-6 py-20 text-center border-none">
                                                    <div className="flex flex-col items-center justify-center">
                                                        <div className="w-16 h-16 bg-[#F0F7FF] rounded-full flex items-center justify-center mb-4 shadow-sm border border-[#E8F2FF]">
                                                            <FiCheckCircle className="w-8 h-8 text-[#133A69]" />
                                                        </div>
                                                        <h3 className="text-base font-bold text-[#133A69] mb-1 tracking-wide">No Requests Found</h3>
                                                        <p className="text-gray-500 text-sm">There are currently no discrepancy requests to review.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <TablePagination
                            component="div"
                            count={filteredRequests.length}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            className="bg-white border-t border-gray-100 placeholder:text-gray-500 rounded-[0px_0px_24px_24px]"
                        />
                    </div>
                </div>

                {/* Review Modal */}
                {isModalOpen && selectedRequest && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px] animate-in fade-in duration-300" onClick={() => setIsModalOpen(false)}></div>
                        <div className="bg-white w-full max-w-sm md:max-w-xl rounded-xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 animate-in zoom-in-95 duration-200">
                            <div className="max-h-min overflow-y-auto custom-scrollbar">
                                {/* Modal Header - Center on mobile, Left on md+ */}
                                <div className="pt-7 pb-3 px-2 md:px-6 md:pt-8 text-center md:text-left relative">
                                    <button
                                        onClick={() => setIsModalOpen(false)}
                                        className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-all text-slate-400 hover:text-slate-700"
                                    >
                                        <FiX size={20} />
                                    </button>
                                    <h2 className="text-lg md:text-2xl font-black text-[#1e293b] tracking-tight">Review Request</h2>
                                    <p className="text-slate-400 text-[12px] font-medium mt-1.5 leading-snug px-6 md:px-0">
                                        Review the details and provide a decision for this correction request.
                                    </p>
                                </div>

                                <div className="px-4 md:px-6 pb-6 pt-2 md:pt-4 space-y-3">
                                    {/* Employee + Target Date - always 2 col */}
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="bg-[#f8fafc] px-2 sm:px-4 py-2 sm:py-3.5 rounded-xl border border-slate-100">
                                            <div className="flex items-center gap-1 sm:gap-1.5 mb-1">
                                                <FiUser size={10} className="text-slate-400" />
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">EMPLOYEE</span>
                                            </div>
                                            <div className="text-xs sm:text-sm font-semibold text-slate-800 truncate">{selectedRequest.userId?.Name || "N/A"}</div>
                                        </div>
                                        <div className="bg-[#f8fafc] px-2 sm:px-4 py-2 sm:py-3.5 rounded-xl border border-slate-100">
                                            <div className="flex items-center gap-1 sm:gap-1.5 mb-1">
                                                <FiCalendar size={10} className="text-slate-400" />
                                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">TARGET DATE</span>
                                            </div>
                                            <div className="text-xs sm:text-sm font-semibold text-slate-800">{moment(selectedRequest.correctionDate).format("YYYY-MM-DD")}</div>
                                        </div>
                                    </div>

                                    {/* Employee's Reason */}
                                    <div className="space-y-2">
                                        <span className="font-bold text-xs text-slate-400 uppercase tracking-widest px-1">EMPLOYEE'S REASON</span>
                                        <div className="bg-[#eef4ff] px-3 py-3 rounded-xl border border-blue-100/60">
                                            <p className="text-xs text-slate-600 font-semibold italic leading-relaxed">
                                                "{selectedRequest.reason}"
                                            </p>
                                        </div>
                                    </div>

                                    {/* Admin Comments */}
                                    <div className="space-y-2">
                                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1">ADMIN COMMENTS</span>
                                        <textarea
                                            value={adminNote}
                                            onChange={(e) => selectedRequest.status === 'Pending' && setAdminNote(e.target.value)}
                                            readOnly={selectedRequest.status !== 'Pending'}
                                            rows="4"
                                            placeholder={selectedRequest.status !== 'Pending' ? `Decision already recorded: ${selectedRequest.status}` : "Add a note for the employee..."}
                                            className={`w-full min-h-[100px] border-1 rounded-xl px-3 py-2 text-sm shadow-sm font-medium outline-none transition-all resize-none ${
                                                selectedRequest.status !== 'Pending'
                                                    ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed placeholder:text-gray-400'
                                                    : 'bg-[#f3f3f5] border-slate-200 text-slate-700 focus:ring-2 focus:ring-slate-200 focus-visible:border-[#A1A1A1] focus-visible:ring-offset-0 placeholder:text-slate-400'
                                            }`}
                                        ></textarea>
                                    </div>

                                    {/* Buttons Section */}
                                    {(() => {
                                        const isProcessed = selectedRequest.status !== 'Pending';
                                        const statusColor = selectedRequest.status === 'Approved'
                                            ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                            : 'bg-red-50 text-red-500 border-red-200';
                                        return (
                                            <>
                                                {isProcessed && (
                                                    <div className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-xl border text-xs font-bold ${statusColor}`}>
                                                        {selectedRequest.status === 'Approved' ? <IoCheckmarkCircleOutline size={18} /> : <FiXCircle size={18} />}
                                                        This request has already been <span className="font-black uppercase">{selectedRequest.status}</span>
                                                    </div>
                                                )}

                                                {/* Mobile: full-width Reject+Approve, then Cancel below */}
                                                <div className="flex md:hidden items-center gap-3">
                                                    <button
                                                        onClick={() => handleAction('Rejected')}
                                                        disabled={isProcessed}
                                                        className={`flex-1 py-1.5 border-2 font-bold text-[12px] rounded-full transition-all flex items-center justify-center gap-2 active:scale-95 ${
                                                            isProcessed
                                                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                                                : 'bg-white border-red-300 text-red-600 hover:bg-red-50'
                                                        }`}
                                                    >
                                                        <FiXCircle size={15} />
                                                        Reject
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction('Approved')}
                                                        disabled={isProcessed}
                                                        className={`flex-1 py-1.5 font-bold text-[12px] rounded-full transition-all flex items-center justify-center gap-2 shadow-md active:scale-95 ${
                                                            isProcessed
                                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                                                                : 'bg-[#059669] text-white hover:bg-[#047857]'
                                                        }`}
                                                    >
                                                        <IoCheckmarkCircleOutline size={15} />
                                                        Approve
                                                    </button>
                                                </div>
                                                <div className="flex md:hidden text-center justify-center">
                                                    <button
                                                        onClick={() => setIsModalOpen(false)}
                                                        className="text-slate-500 font-black text-sm hover:text-slate-700 transition-colors py-1"
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </>
                                        );
                                    })()}

                                    {/* Desktop md+: Cancel left, Reject+Approve right */}
                                    {(() => {
                                        const isProcessed = selectedRequest.status !== 'Pending';
                                        return (
                                            <div className="hidden md:flex items-center justify-between pt-3 border-t border-gray-50">
                                                <button
                                                    onClick={() => setIsModalOpen(false)}
                                                    className="text-slate-500 font-black text-sm hover:text-slate-800 hover:bg-slate-50 px-5 py-2.5 rounded-full transition-all"
                                                >
                                                    Cancel
                                                </button>
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        onClick={() => handleAction('Rejected')}
                                                        disabled={isProcessed}
                                                        title={isProcessed ? `Already ${selectedRequest.status}` : 'Reject this request'}
                                                        className={`px-6 py-3 border-2 font-black text-sm rounded-full transition-all flex items-center gap-2 active:scale-95 ${
                                                            isProcessed
                                                                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                                                                : 'bg-white border-red-200 text-red-500 hover:bg-red-50'
                                                        }`}
                                                    >
                                                        <FiXCircle size={17} />
                                                        Reject
                                                    </button>
                                                    <button
                                                        onClick={() => handleAction('Approved')}
                                                        disabled={isProcessed}
                                                        title={isProcessed ? `Already ${selectedRequest.status}` : 'Approve this request'}
                                                        className={`px-6 py-3 font-black text-sm rounded-full transition-all flex items-center gap-2 ${
                                                            isProcessed
                                                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                                                                : 'bg-[#059669] text-white hover:bg-[#047857] shadow-md active:scale-95'
                                                        }`}
                                                    >
                                                        <IoCheckmarkCircleOutline size={17} />
                                                        Approve
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default AttendanceCorrections;
